import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { OnboardingService } from '../services/onboarding.service';
import { ProductionService } from '../services/production.service';

/** Validates the :slug path segment and invalidates tenant-scoped caches
 * whenever the slug changes (i.e. the user switches between tenants).
 *
 * When a tenant switch is detected, navigation is blocked until fresh
 * account and bootstrap data have been fetched for the new tenant.
 *
 * The tenant header is added to API requests by tenantHeaderInterceptor,
 * which derives the slug directly from the live router URL.
 */
@Injectable({ providedIn: 'root' })
export class SlugTenantGuard implements CanActivate {
  private currentSlug: string | null = null;

  constructor(
    private accountService: AccountService,
    private productionService: ProductionService,
    private onboardingService: OnboardingService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    const slug = route.params['slug'];
    if (typeof slug !== 'string' || !/^[a-z0-9-]{2,64}$/.test(slug)) {
      return false;
    }

    if (this.currentSlug !== null && this.currentSlug !== slug) {
      this.accountService.reset();
      this.productionService.reset();
      this.onboardingService.reset();
      this.currentSlug = slug;

      return forkJoin([
        this.accountService.refresh(),
        this.productionService.loadBootstrap().pipe(catchError(() => of(null))),
      ]).pipe(
        map(() => true),
        catchError(() => of(false))
      );
    }

    this.currentSlug = slug;
    return true;
  }
}
